/**
 * URL-Tracker: speichert die aktuelle URL (pathname + search, ohne Hash).
 * Verändert die URL nie – rein passiv. F5 bleibt auf derselben Seite.
 */

export const LAST_PAGE_KEY = 'fyle_last_page';

/** Pfade, die nicht als letzte Seite gespeichert werden */
const SKIP_PATHS = [
  '/login',
  '/signup',
  '/reset-password',
  '/reset-password-request',
  '/verify-email',
  '/email-change-confirm',
  '/error-denied',
  '/auth/dropbox/callback',
  '/billing/success',
  '/billing/cancel',
  '/pricing',
  '/share',
];

/** Mapping Pfad → ein Wort (für Kompatibilität / Auswertung) */
const PATH_TO_PAGE: Array<{ match: string | RegExp; page: string }> = [
  { match: '/dashboard', page: 'dashboard' },
  { match: '/cloud-explorer', page: 'cloud' },
  { match: '/sharing', page: 'sharing' },
  { match: '/version', page: 'version' },
  { match: '/settings', page: 'settings' },
  { match: '/billing', page: 'billing' },
  { match: '/support', page: 'support' },
  { match: '/youtube', page: 'youtube' },
  { match: '/upload', page: 'upload' },
  { match: '/admin', page: 'admin' },
];

function shouldSkip(pathname: string): boolean {
  if (!pathname || pathname === '/') return true;
  return SKIP_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

/**
 * Prüft, ob der Pfad getrackt werden soll (gibt Page-Key zurück oder null).
 */
export function pathToPageKey(pathname: string): string | null {
  if (shouldSkip(pathname)) return null;
  const path = pathname.replace(/\/$/, '') || '/';
  for (const { match, page } of PATH_TO_PAGE) {
    if (typeof match === 'string') {
      if (path === match || path.startsWith(match + '/')) return page;
    } else {
      if (match.test(path)) return page;
    }
  }
  return null;
}

/**
 * Speichert die volle Route (pathname + search, ohne Hash). Keine URL-Änderung.
 * Speichert "/" nicht, damit der letzte gültige Pfad beim Reload erhalten bleibt.
 */
export function setLastPath(fullPath: string): void {
  try {
    const path = fullPath.split('#')[0];
    if (path && path.startsWith('/') && path !== '/' && pathToPageKey(path.split('?')[0]) != null) {
      localStorage.setItem(LAST_PAGE_KEY, path);
    }
  } catch {
    // localStorage nicht verfügbar
  }
}

/**
 * Liest die zuletzt gespeicherte Route (pathname + search, nie mit Hash).
 */
export function getLastPath(): string | null {
  try {
    return localStorage.getItem(LAST_PAGE_KEY);
  } catch {
    return null;
  }
}

/** @deprecated Nutze getLastPath() – Kompatibilität */
export function getLastPage(): string | null {
  return getLastPath();
}

/** Mapping Page-Key → Standardpfad */
const PAGE_TO_PATH: Record<string, string> = {
  dashboard: '/dashboard',
  cloud: '/cloud-explorer',
  sharing: '/sharing',
  version: '/version',
  settings: '/settings',
  billing: '/billing',
  support: '/support',
  youtube: '/youtube',
  upload: '/cloud-explorer',
  admin: '/admin',
};

export function getPathFromPageKey(pageKey: string): string | null {
  return PAGE_TO_PATH[pageKey] ?? null;
}

/**
 * Prüft, ob ein gespeicherter Pfad für Redirect erlaubt ist (kein Auth/Public).
 */
export function isAllowedRedirectPath(path: string | null): boolean {
  if (!path || !path.startsWith('/')) return false;
  const pathname = path.split('?')[0];
  return !shouldSkip(pathname);
}
